import { forEach, get, isArray, isNil, map, some } from 'lodash';
import { sendSignedDocument } from '../../services';
import { sendDeclaration } from '../../services/API/3ndflXml';
import { sendAttachments } from '../../services/API/3ndflView';

/**
 * Prepared documents data to sending.
 * If has inventory xml will be created additional document for signing and sending transport
 * container containing attachments documents.
 * Without inventory xml will be created single document containing attachments inside itself.
 *
 * By password field we can understand expected structure of documents dependent on sign type
 *
 * @type {Function}
 * @param {Object} requestData - users document to signing. Can be array of objects or single object.
 * @param {Array} attachments - users attachments. Can be array of arrays objects or array of objects.
 * @param {string} password - sign password. Used only for  signing on server
 *
 * @param signTogether - data should be signed together (main docuemnt with attachments)
 * @return {{ inventory: Array, issues: Array }}
 */
export function prepareRequestData(requestData, attachments, password, signTogether) {
  const documents = [].concat(requestData);
  const inventory = [];
  const issues = [];

  forEach(documents, (data, index) => {
    const itemAttachments = isArray(requestData) ? get(attachments, index) : attachments;

    if (signTogether) {
      issues.push(issueWithInventoryDocumentStructure(data, itemAttachments, password));
    } else if (isNil(get(data, 'inventoryXml'))) {
      issues.push(issueDocumentStructure(data, itemAttachments, password));
    } else {
      inventory.push(inventoryDocumentStructure(data, itemAttachments, password));
      issues.push(issueDocumentStructure(data, [], password));
    }
  });

  return {
    inventory,
    issues,
  };
}

/**
 * @type Function
 * @param {Object} data - users document.
 * @param {string} data.cardId
 * @param {string} data.pdf
 * @param {string} data.inventoryXml
 * @param {string} data.signInventoryXml
 * @param itemAttachments — users attachments associated with current document.
 * @param password
 * @return {Object}
 */
export function inventoryDocumentStructure(data = {}, itemAttachments, password) {
  const { cardId, pdf, inventoryXml, signInventoryXml, hint } = data;
  if (password) {
    return {
      data: inventoryXml,
      pin: password,
      cardId,
      pdf,
      hint,
      attachedSignedDocuments: mapRequestAttachments(itemAttachments),
    };
  } else {
    return {
      data: inventoryXml,
      sign: signInventoryXml,
      cardId,
      pdf,
      hint,
      attachedSignedDocuments: itemAttachments,
    };
  }
}

/**
 * @type Function
 * @param {Object} data - users document.
 * @param {string} data.cardId
 * @param {string} data.pdf
 * @param {string} data.xml
 * @param {string} data.signXml
 * @param {Array} itemAttachments — users attachments associated with current document.
 * @param password
 * @return {Object}
 */
export function issueDocumentStructure(data = {}, itemAttachments, password) {
  const { xml, cardId, pdf, signXml, hint } = data;

  if (password) {
    return {
      data: xml,
      pin: password,
      cardId,
      pdf,
      hint,
      attachedSignedDocuments: mapRequestAttachments(itemAttachments),
    };
  } else {
    return {
      data: xml,
      sign: signXml,
      cardId,
      pdf,
      hint,
      attachedSignedDocuments: itemAttachments,
    };
  }
}

/**
 * @type Function
 * @param {Object} data - users document.
 * @param {string} data.cardId
 * @param {string} data.pdf
 * @param {string} data.xml
 * @param {string} data.signXml
 * @param {Array} itemAttachments — users attachments associated with current document.
 * @param pin
 * @return {Object}
 */
export function issueWithInventoryDocumentStructure(data = {}, itemAttachments, pin) {
  const { xml, cardId, pdf, signXml, hint, inventoryXml, signInventoryXml, pdfSign } = data;
  let signDataFields;

  if (pin) {
    signDataFields = { pin };
  } else {
    signDataFields = {
      sign: signXml,
      inventorySign: signInventoryXml,
      pdfSign,
    };
  }

  return {
    cardId,
    data: xml,
    inventoryData: inventoryXml,
    pdf,
    hint,
    attachedSignedDocuments: pin ? mapRequestAttachments(itemAttachments) : itemAttachments,
    ...signDataFields,
  };
}

/**
 * @type Function
 * @param attachments
 * @return {any[] | Array<any[function(*): {fileLink: string, fileName: string}]>}
 */
export function mapRequestAttachments(attachments) {
  return map(attachments, (attach) => {
    const file = {
      fileLink: attach.serverFileName || attach.fileLink,
      fileName: attach.name || attach.fileName,
    };
    if (attach.attributes) {
      file.attributes = attach.attributes;
    }
    if (attach.additionalInfo) {
      file.additionalInfo = attach.additionalInfo;
    }
    return file;
  });
}

export function checkRequestIsOk(signResponse) {
  if (!get(signResponse, 'isOk')) {
    throw new Error(get(signResponse, 'error.message'));
  }
}

export function checkRequestStatus(signResponse) {
  const WRONG_PIN_STATUS = 'WRONG_PIN';
  signResponse = [].concat(get(signResponse, 'data'));
  if (some(signResponse, { status: WRONG_PIN_STATUS })) {
    throw new Error(WRONG_PIN_STATUS);
  }
}

/**
 * It helps to correctly choosing request for documents sending.
 * If have 'reportYear' parameter, then will be chosen ndfl3 request with year data.
 * Else using default request.
 *
 * @param {{ [reportYear]: string, [onlyAttachments]: boolean  }} customRequest — object with custom parameters.
 * @return {Function}
 */
export function getRequestEndpoint(customRequest) {
  const declarationYear = get(customRequest, 'reportYear');
  if (declarationYear) {
    return sendDeclaration(declarationYear);
  }
  if (get(customRequest, 'onlyAttachments')) {
    return sendAttachments;
  }
  return sendSignedDocument;
}

/**
 * Function sign attachments array by client side.
 * After signing function will create common array of files objects with additional field 'sign', or throw an error
 *
 * @param {(Array| Object)} attachments
 * @param signService
 * @return {Promise}
 */
export function signAttachments(attachments, signService) {
  const attachmentsArray = [].concat(attachments);

  if (!attachmentsArray.length) {
    return Promise.resolve([]);
  }

  return Promise.all(
    map(attachmentsArray, (attach) => {
      return signService
        .signCreate(attach.byteArrays, { fileName: attach.fileName })
        .then((result) => {
          const file = {
            fileLink: attach.serverFileName || attach.fileLink,
            fileName: attach.name || attach.fileName,
            sign: result,
          };
          if (attach.attributes) {
            file.attributes = attach.attributes;
          }
          return file;
        });
    }),
  ).catch((error) => {
    throw new Error(`Ошибка подписания приложения ИД: ${error}`);
  });
}

/**
 * Function sign xml and inventory xml documents by client side.
 * Signature will be added at original object 'document'.
 *
 * @param {Object} document
 * @param signService
 * @return {Promise}
 */
export function signDocuments(document, signService) {
  const { xml, inventoryXml } = document;
  const promiseArray = [];
  if (xml) promiseArray.push(signService.signCreate(xml, { isEncodedData: true }));
  if (inventoryXml)
    promiseArray.push(signService.signCreate(inventoryXml, { isEncodedData: true }));
  return Promise.all(promiseArray)
    .then(([xml, inventoryXml]) => {
      return {
        ...document,
        signXml: xml,
        signInventoryXml: inventoryXml,
      };
    })
    .catch((error) => {
      throw new Error(`Ошибка подписания документа ИД: ${error.message}`);
    });
}
